import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router';
import { GetRouteDetails, GetRouteStatuses, ReroutePickups, GetPickupDetails } from '../../../services/pickupsService';
import { GetRoutesInSameTerritory } from '../../../services/tpcPortalService';
import { RoutePickup, PickupFullSumary } from '../../../models/RoutePickup';
import { PickupType } from '../../../models/PickupType';
import { PickupStatus } from '../../../models/PickupStatus';

import { ReactComponent as ClockIcon } from '../../../images/clock-solid.svg';
import { ReactComponent as PhoneIcon } from '../../../images/phone-solid.svg';
import { ReactComponent as DropBoxIcon } from '../../../images/dropbox.svg';
import { ReactComponent as ChevronRightIcon } from '../../../images/chevron-right-solid.svg';

import { compareTime } from '../../../functions/datetime';

import { PickupDetailed } from '../../../models/PickupDetailed';
import { RoutePickupStatus } from '../../../models/RoutePickup';

import OptionPicker from '../../Common/OptionPicker';
import LabeledProperty from './LabeledProperty';

function getTypeIcon(pickupType: string): React.ReactElement {
  if (pickupType.toLowerCase() === PickupType.scheduled.toLowerCase())
    return <ClockIcon fill="white" className="h-10 w-10 rounded-lg bg-lime-600 p-2" />;
  if (pickupType.toLowerCase() === PickupType.onCall.toLowerCase())
    return <PhoneIcon fill="white" className="h-10 w-10 rounded-lg bg-amber-600 p-2" />;
  if (pickupType.toLowerCase() === PickupType.dropBox.toLowerCase())
    return <DropBoxIcon fill="white" className="h-10 w-10 rounded-lg bg-sky-600 p-1" />;

  return <React.Fragment />;
}

/**
 *
 * @param window string i.e '8:00 AM - 5:00 PM`
 * @returns tuple ['8:00 AM', '5:00 PM']
 */
function splitTimeWindow(window: string): [string, string] {
  if (!window || !window.includes(' - ')) return ['', ''];
  const w = window.split(' - ').map((e) => e.trim());

  return [w[0], w[1]];
}

function sortPickupsByTimeWindow(a: RoutePickup, b: RoutePickup) {
  if (!a.pickupWindow.includes(' - ') || !b.pickupWindow.includes(' - ')) return 0;

  const aRange = a.pickupWindow.split(' - ');
  const bRange = b.pickupWindow.split(' - ');

  const startComparison = compareTime(aRange[0], bRange[0]);

  if (startComparison === 0) {
    return compareTime(aRange[1], bRange[1]);
  }

  return startComparison;
}

function PickupRow({ pickup, isOdd }: { pickup: PickupFullSumary; isOdd: boolean }) {
  const [isExpanded, setExpanded] = useState(false);
  const [pickupDetails, setPickupDetails] = useState<PickupDetailed | undefined>(undefined);

  const toggleExpansion = (isOpen: boolean) => {
    if (isOpen && !pickupDetails)
      GetPickupDetails(pickup.addressid, pickup.stopType).then((details) => {
        if (details) setPickupDetails(details);
      });

    setExpanded(isOpen);
  };

  const getBorderColor = (status: string): string => {
    status = status.toLowerCase();

    if (status === PickupStatus.in_progress.toLowerCase()) return 'border-blue-500';
    if (status === PickupStatus.attempted.toLowerCase()) return 'border-amber-500';

    if (status === PickupStatus.cancelled.toLowerCase()) return 'border-red-500';
    if (status === PickupStatus.completed.toLowerCase()) return 'border-lime-600';
    if (status === PickupStatus.missed.toLowerCase()) return 'border-purple-500';

    return '';
  };

  const image = getTypeIcon(pickup.stopType);
  const timeWindow = splitTimeWindow(pickup.pickupWindow);

  return (
    <div
      className={`flex grow flex-col border-l-8 ${getBorderColor(pickup.status)} ${
        isExpanded ? 'border-b-2 bg-blue-200' : ''
      }`}>
      <div
        onClick={() => toggleExpansion(!isExpanded)}
        className={`grid grid-cols-12 py-1 hover:cursor-pointer md:text-lg ${
          isExpanded ? '' : isOdd ? 'bg-slate-200' : 'bg-white'
        } hover:bg-blue-300`}>
        <div className="col-span-4 flex items-center justify-center font-semibold sm:text-xl md:col-span-3 md:text-xl lg:text-2xl">
          <div className="hidden md:flex">{pickup.pickupWindow}</div>
          <div className="flex flex-col space-y-0 text-center md:hidden">
            <div>{timeWindow[0]}</div>
            <div className="text-xs">to</div>
            <div>{timeWindow[1]}</div>
          </div>
        </div>

        <div className="col-span-8 hidden flex-row md:flex">
          <div className="flex w-3/5 flex-col pl-2">
            <div className="font-semibold">{pickup.companyName}</div>
            <div>{pickup.address1}</div>
            <div className={`${!isExpanded ? 'hidden' : ''}`}>{pickup.address2}</div>
          </div>

          <div className="flex w-2/5 flex-col pl-2">
            <div>{pickup.city}</div>
            <div className=" font-semibold">{pickup.zip}</div>
          </div>
        </div>

        <div className="col-span-7 flex flex-col pl-2 md:hidden">
          <div className="font-semibold">{pickup.companyName}</div>
          <div>{pickup.address1}</div>
          <div>
            {pickup.city}, {pickup.state} {pickup.zip}
          </div>
        </div>

        <div className="hidden justify-end pt-2 pr-2 md:flex">{image}</div>
      </div>
      {isExpanded && <PickupExpandedDetails pickup={pickupDetails} />}
    </div>
  );
}

function PickupExpandedDetails({ pickup }: { pickup: PickupDetailed | undefined }) {
  if (!pickup) return <></>;

  return (
    <div className="flex flex-col bg-blue-200">
      <div className="flex flex-row items-center justify-between pr-10 pt-5 text-white">
        <div className="bg-blue-500 py-1 pl-10  pr-5 text-xl font-bold shadow-md">{pickup.pickupStatus}</div>
        <div className="rounded-full bg-blue-500 px-3 text-lg font-semibold uppercase text-white">
          {pickup.locationType === 'D' && 'DROPBOX'}
          {pickup.locationType === 'O' && 'ON-CALL'}
          {pickup.locationType === 'S' && 'SCHEDULED'}
        </div>
      </div>

      <div className="flex grow flex-col py-5 pl-16 text-lg ">
        {<LabeledProperty title="ETA" value={pickup.eta} timeString={true} />}
        {<LabeledProperty title="location ID" value={pickup.locationId} />}
        {<LabeledProperty title="driver #" value={pickup.driverNumber} />}
        {<LabeledProperty title="account name" value={pickup.accountName} />}
        {<LabeledProperty title="special instructions" value={pickup.specialInstructions} />}
        {<LabeledProperty title="attempt reason" value={pickup.attemptReason} />}
        {<LabeledProperty title="door hanger #" value={pickup.doorHangerNumber} />}
      </div>
      <div className="flex grow flex-col py-5 pl-16 text-lg ">
        {<LabeledProperty title="scheduled time" value={pickup.scheduleTime} timeString={true} />}
        {<LabeledProperty title="cancelled time" value={pickup.cancelledTime} timeString={true} />}
        {<LabeledProperty title="pickedup time" value={pickup.pickupedTime} timeString={true} />}
        {<LabeledProperty title="attempted time" value={pickup.attemptedTime} timeString={true} />}
        {<LabeledProperty title="ready time" value={pickup.readyTime} timeString={true} />}
        {<LabeledProperty title="latest time" value={pickup.latestTime} timeString={true} />}
        {<LabeledProperty title="close time" value={pickup.closeTime} timeString={true} />}
      </div>
      <div className="flex grow flex-col py-5 pl-16 text-lg ">
        {<LabeledProperty title="scanned packages" value={pickup.scannedPkgCount} />}
        {<LabeledProperty title="scanned pallets" value={pickup.scannedPalletCount} />}
        {<LabeledProperty title="manual packages" value={pickup.manualPkgCount} />}
        {<LabeledProperty title="manual pallets" value={pickup.manualPalletCount} />}
        {<LabeledProperty title="pieces picked" value={pickup.piecesPicked} />}
        {<LabeledProperty title="total weight" value={pickup.totalWeight} />}
      </div>
    </div>
  );
}

function RoutePickupDetailsView() {
  const { routeNumber } = useParams();

  const [routePickups, setRoutePickups] = useState<PickupFullSumary[]>([]);
  const [routesInTerritory, setRoutesInTerritory] = useState<string[]>([]);
  const [selectedPickups, setSelectedPickups] = useState<string[]>([]);

  const [selectedRoute, setSelectedRoute] = useState<string | undefined>();
  const [routeSelectionOpen, setRouteSelectionOpen] = useState(false);

  const getRouteInfo = useCallback(async () => {
    if (routeNumber) {
      Promise.all([GetRouteDetails(routeNumber), GetRouteStatuses(routeNumber)]).then(([details, statuses]) => {
        const statusMap = statuses.reduce<Record<number, RoutePickupStatus>>((acc, e) => {
          acc[e.locationID] = e;
          return acc;
        }, {});

        const pickups = details.map((pickup) => {
          // If pickupWindow expected format, replace double spaces
          const timeWindow = pickup.pickupWindow.includes(' - ')
            ? pickup.pickupWindow.replace(/  +/g, ' ').replace(' AM', 'AM').replace(' PM', 'PM')
            : pickup.pickupWindow;

          return {
            ...statusMap[pickup.addressid],
            ...pickup,
            timeWindow,
          };
        });

        setRoutePickups(pickups.sort(sortPickupsByTimeWindow));
      });
    }
  }, [routeNumber]);

  useEffect(() => {
    if (routeNumber) {
      GetRoutesInSameTerritory(routeNumber).then((routes) => {
        if (routes.length)
          setRoutesInTerritory(
            routes
              .sort()
              .map((r) => r.toUpperCase())
              .filter((r) => r !== routeNumber.toUpperCase()), // We don't need to move this to same route
          );
      });
    }
  }, [routeNumber]);

  useEffect(() => {
    getRouteInfo();
  }, [routeNumber, getRouteInfo]);

  const reroute = () => {
    if (!routeNumber || !selectedRoute) return;

    const stops = selectedPickups.map((stop) => {
      const [type, locationId] = stop.split('=');
      return { type, locationId: parseInt(locationId) };
    });

    ReroutePickups(routeNumber, selectedRoute, stops).then((res) => {
      if (!res) return;

      getRouteInfo();
      alert(`Rerouted to ${selectedRoute}`);
      setSelectedRoute(undefined);
      setSelectedPickups([]);
    });
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, value } = event.target;

    if (checked) setSelectedPickups([...selectedPickups, value]);
    else setSelectedPickups(selectedPickups.filter((e) => e !== value));
  };

  const pickupIsReroutable = (pickup: PickupFullSumary): boolean => {
    return pickup.status === PickupStatus.in_progress || pickup.status === PickupStatus.missed;
  };

  return (
    <div className="mx-0 max-w-[1000px] md:mx-auto">
      <OptionPicker
        title="Select route to move to"
        options={routesInTerritory}
        isOpen={routeSelectionOpen}
        setIsOpen={setRouteSelectionOpen}
        selectOption={setSelectedRoute}
      />
      <div className="mt-5 mb-2 flex flex-row justify-between text-xl font-bold text-blue-800 md:text-3xl">
        <h1>Pickups</h1>
        <h1>
          Route <span>{routeNumber}</span>
        </h1>
      </div>
      <div className="flex grow flex-row bg-slate-600">
        <div className="flex grow">
          <div className="grid grow grid-cols-12 space-x-1 py-1 font-mono uppercase text-white drop-shadow-md sm:text-lg md:text-2xl">
            <div className="col-span-4 pl-2 md:col-span-3">Time Window</div>
            <div className="col-span-8 hidden flex-row md:flex">
              <div className="w-3/5">Company Name/Address</div>
              <div className="w-2/5">City/Zip</div>
            </div>
            <div className="col-span-7 flex justify-center md:hidden md:justify-start">Address</div>

            <div className="justify-end">Reroute</div>
          </div>
        </div>
        <div className="w-14"></div>
      </div>
      {routePickups.map((pickup, idx) => {
        return (
          <div key={pickup.addressid} className="flex border-b-2 border-b-white">
            <PickupRow pickup={pickup} isOdd={idx % 2 === 0} />
            <div className="w-10 bg-gray-300 px-2 md:w-14">
              <input
                type="checkbox"
                name="selectedRoutes"
                value={`${pickup.stopType}=${pickup.addressid}`}
                disabled={!pickupIsReroutable(pickup)}
                onChange={handleCheckboxChange}
                className={`mt-3 h-6 w-6 rounded text-blue-600 focus:ring-2 md:h-10 md:w-10`}
              />
            </div>
          </div>
        );
      })}
      <div className="my-3 mx-2 flex justify-end">
        <div className="flex flex-col space-y-3">
          <button
            onClick={() => setRouteSelectionOpen(true)}
            className={`btn-primary bg-sky-200 capitalize ${routePickups.length < 1 && 'hidden'}`}
            disabled={!selectedPickups.length}>
            {selectedRoute && 're'}select route
          </button>
          <button
            onClick={() => reroute()}
            className={`btn-primary flex flex-row items-center ${!selectedRoute && 'hidden'}`}
            disabled={!selectedPickups.length || !selectedRoute}>
            Reroute to {selectedRoute} <ChevronRightIcon fill="#333333" className="h-8 pl-2" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default RoutePickupDetailsView;
